import moment from 'moment';

import {TimeRange, TimeScale, DateRange, DateMoment, PreviousPeriod} from '../../types';
import {getLocale} from 'epic-common/src/locale';

export const CODES = {
    [TimeRange.WEEK]: 'epic.affiliate.fortnite_games.discret_range_picker.week',
    [TimeRange.FORTNIGHT]: 'epic.affiliate.fortnite_games.discret_range_picker.fortnight',
    [TimeRange.MONTH]: 'epic.affiliate.fortnite_games.discret_range_picker.month',
    [TimeRange.THREE_MONTHS]: 'epic.affiliate.fortnite_games.discret_range_picker.three_months',
    [TimeRange.TIME_RANGE]: 'epic.affiliate.fortnite_games.discret_range_picker.select_time_range',
    [PreviousPeriod.TWO_WEEKS]: 'epic.affiliate.fortnite_games.discret_range_picker.two_weeks',
    [PreviousPeriod.FOUR_WEEKS]: 'epic.affiliate.fortnite_games.discret_range_picker.four_weeks',
    defaultTimeRangeLabel: 'epic.affiliate.fortnite_games.date_select.default'
};

export const weeklyEndDate = (dateNow: moment.Moment) =>
    moment(dateNow)
        .subtract(7, 'days')
        .endOf('week');

export const monthlyEndDate = (dateNow: moment.Moment) =>
    moment(dateNow)
        .subtract(1, 'months')
        .endOf('month');

export interface GetTimeRangeDefaultLabelProps {
    timeRange: TimeRange;
    defaultTimeRange: TimeRange;
    label: string;
}

export const getTimeRangeDefaultLabel = ({
    timeRange,
    defaultTimeRange,
    label
}: GetTimeRangeDefaultLabelProps): string => (timeRange === defaultTimeRange ? label : '');

export interface GetPreviousPeriodProps {
    category: TimeScale;
    timeRange: TimeRange;
}

export const getPreviousPeriod = ({
    category,
    timeRange
}: GetPreviousPeriodProps): PreviousPeriod => {
    if (category === TimeScale.WEEKLY && timeRange === TimeRange.FORTNIGHT) {
        return PreviousPeriod.TWO_WEEKS;
    } else if (category === TimeScale.WEEKLY && timeRange === TimeRange.MONTH) {
        return PreviousPeriod.FOUR_WEEKS;
    }
};

export interface GetTimeRangeLabelProps extends GetPreviousPeriodProps {
    defaultTimeRange: TimeRange;
    getMessage: (code: string) => string;
}

export const getTimeRangeLabel = ({
    category,
    timeRange,
    defaultTimeRange,
    getMessage
}: GetTimeRangeLabelProps): string => {
    const label = getTimeRangeDefaultLabel({
        timeRange,
        defaultTimeRange,
        label: getMessage(CODES.defaultTimeRangeLabel)
    });

    const previousPeriod = getPreviousPeriod({category, timeRange});

    switch (previousPeriod) {
        case PreviousPeriod.TWO_WEEKS:
            return getMessage(CODES[PreviousPeriod.TWO_WEEKS]);

        case PreviousPeriod.FOUR_WEEKS:
            return `${getMessage(CODES[PreviousPeriod.FOUR_WEEKS])} ${label}`;

        default:
            return `${getMessage(CODES[timeRange])} ${label}`;
    }
};

export const getDefaultTimeRange = (category: TimeScale): TimeRange => {
    switch (category) {
        case TimeScale.WEEKLY:
            return TimeRange.MONTH;

        case TimeScale.MONTHLY:
            return TimeRange.THREE_MONTHS;

        default:
            return TimeRange.FORTNIGHT;
    }
};

export const getTimeRangeDates = (timeRange: TimeRange, category?: TimeScale): DateRange => {
    const dateNow = moment();
    const startDate = moment(dateNow);

    switch (timeRange) {
        case TimeRange.FORTNIGHT:
            if (category === TimeScale.WEEKLY) {
                return {
                    startDate: startDate.subtract(14, 'days').startOf('week'),
                    endDate: weeklyEndDate(dateNow),
                    selectedDate: weeklyEndDate(dateNow)
                };
            } else {
                return {
                    startDate: startDate.subtract(14, 'days'),
                    endDate: dateNow,
                    selectedDate: dateNow
                };
            }

        case TimeRange.MONTH:
            if (category === TimeScale.WEEKLY) {
                return {
                    startDate: startDate.subtract(4, 'weeks').startOf('week'),
                    endDate: weeklyEndDate(dateNow),
                    selectedDate: weeklyEndDate(dateNow)
                };
            } else {
                return {
                    startDate: startDate.subtract(1, 'months').startOf('month'),
                    endDate: monthlyEndDate(dateNow),
                    selectedDate: monthlyEndDate(dateNow)
                };
            }

        case TimeRange.THREE_MONTHS:
            return {
                startDate: startDate.subtract(3, 'months').startOf('month'),
                endDate: monthlyEndDate(dateNow),
                selectedDate: monthlyEndDate(dateNow)
            };

        default:
            return {
                startDate: startDate.subtract(7, 'days'),
                endDate: dateNow,
                selectedDate: dateNow
            };
    }
};

interface FormatStartDateParams extends DateRange {
    locale: string;
}

export interface DatelineParams extends DateRange {
    message?: string;
    locale?: string;
}

export type IntervalDateFormatter = (params: {
    locale: string;
    showYear?: boolean;
}) => (date: DateMoment) => string;

const intervalDateFormatter: IntervalDateFormatter = ({locale, showYear}) => {
    const formatter = new Intl.DateTimeFormat(locale, {
        day: 'numeric',
        month: 'short',
        ...(showYear && {year: 'numeric'})
    });

    return date => formatter.format(date.valueOf());
};

const localesWithDifferentYearOrder = ['ja', 'ko'];

const formatIntervalStartDate = ({startDate, endDate, locale}: FormatStartDateParams) =>
    intervalDateFormatter({
        locale,
        showYear:
            !moment(startDate).isSame(endDate, 'year') ||
            localesWithDifferentYearOrder.includes(locale) ||
            moment(startDate).isSame(endDate, 'day')
    })(startDate);

const formatIntervalEndDate = ({startDate, endDate, message, locale}: DatelineParams) => {
    if (moment(startDate).isSame(new Date(), 'day')) {
        return message;
    }

    if (!endDate || moment(endDate).isSame(startDate, 'day')) {
        return '';
    }

    return ` - ${intervalDateFormatter({
        locale,
        showYear:
            !moment(startDate).isSame(endDate, 'year') ||
            !localesWithDifferentYearOrder.includes(locale)
    })(endDate)}`;
};

export const getDateline = ({
    startDate,
    endDate,
    message,
    locale = getLocale()
}: DatelineParams): string =>
    `${formatIntervalStartDate({startDate, endDate, locale})} ${formatIntervalEndDate({
        startDate,
        endDate,
        message,
        locale
    })}`;

export const TIME_RANGES = Object.values(TimeRange);

export interface UseTimeRangeData {
    timeRanges: TimeRange[];
    defaultTimeRange: TimeRange;
}

export const getTimeRange = (category: TimeScale): UseTimeRangeData => {
    switch (category) {
        case TimeScale.WEEKLY:
            return {
                timeRanges: TIME_RANGES.slice(1),
                defaultTimeRange: getDefaultTimeRange(category)
            };

        case TimeScale.MONTHLY:
            return {
                timeRanges: TIME_RANGES.slice(2),
                defaultTimeRange: getDefaultTimeRange(category)
            };

        default:
            return {timeRanges: TIME_RANGES, defaultTimeRange: getDefaultTimeRange(category)};
    }
};

export const isPreviousMonthMonday = (date: DateMoment): boolean => {
    const now = moment();
    const mDate = moment(date);
    return mDate.weekday() === 0 && !mDate.isSame(now, 'month') && !mDate.isAfter(now, 'day');
};

export const isSameDateRange = (dateRange: DateRange, newDateRange: DateRange): boolean => {
    const {startDate, endDate} = dateRange;
    const {startDate: newStartDate, endDate: newEndDate} = newDateRange;

    return (
        moment(startDate).isSame(newStartDate, 'day') && moment(endDate).isSame(newEndDate, 'day')
    );
};

export const isUniqueDateRangeForCategory = (
    previousTimeRangeDates: DateRange,
    activeTimeRangeDates: DateRange,
    previousCategory: TimeScale,
    activeCategory: TimeScale
): boolean => {
    return !(
        isSameDateRange(previousTimeRangeDates, activeTimeRangeDates) &&
        previousCategory === activeCategory
    );
};

export const isFuture = (date: moment.Moment) => date.isAfter(moment(), 'day');

export const getYearMonthDate = (category: TimeScale): DateMoment =>
    category === TimeScale.MONTHLY ? moment().subtract(1, 'months') : moment();
